/**
 * #4401 — the prediction-market odds card: what the room SEES while the
 * companion speaks a market answer.
 *
 * A whitelisted face, built server-side from the one market fact the companion
 * was given for the turn — the same fact that renders its prompt's MARKET
 * lines — so the screen can never show a number the companion was not allowed
 * to quote. No URLs, provider objects or user text: labels, venues and
 * percentages only.
 *
 * Carried two ways: as the payload of the `market_odds` townhall stage overlay,
 * and on the standard-room context update for the search-results panel.
 */
export type MarketOddsVenue = 'polymarket' | 'kalshi';

export interface MarketOddsRow {
    /** The proposition the price is for, e.g. "Will JD Vance win the 2028 US Presidential Election?". */
    label: string;
    venue: MarketOddsVenue;
    /** Whole percent, 0–100. */
    percent: number;
}

export interface MarketOddsCard {
    /** What was asked about — the turn's market subject, e.g. "Fed cuts rates October". */
    heading: string;
    /** Up to two rows per venue, Polymarket first. Never averaged or merged across venues. */
    rows: MarketOddsRow[];
    /** When the prices were fetched (epoch ms). */
    asOf: number;
    /** The answering turn's utterance id, so a late card for a superseded turn can be recognised. */
    utteranceId?: string;
}

/** Display names, as the card and the companion say them. */
export const MARKET_ODDS_VENUE_NAMES: Record<MarketOddsVenue, string> = {
    polymarket: 'Polymarket',
    kalshi: 'Kalshi',
};

/**
 * #4401 — how a STANDARD room shows a turn's odds, chosen by the host.
 *
 * - `cue`: an "Odds" pill on the companion's tile; people open the panel
 *   themselves. The default: nobody's view changes unless they ask.
 * - `panel`: the search-results panel opens by itself for everyone when the
 *   answer starts, showing the odds; no pill.
 *
 * Townhall rooms are unaffected: they show the `market_odds` stage card.
 */
export type MarketOddsDisplay = 'cue' | 'panel';

export const DEFAULT_MARKET_ODDS_DISPLAY: MarketOddsDisplay = 'cue';

/** Payload of `SocketEvents.MARKET_ODDS_DISPLAY_SET` / `MARKET_ODDS_DISPLAY_CHANGED`. */
export interface MarketOddsDisplayPayload {
    roomId: string;
    display: MarketOddsDisplay;
}
